import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  BookOpen, GraduationCap, ArrowRight, Loader2,
  History, Globe, Scale, TrendingUp, Beaker,
  ChevronRight, CheckCircle2
} from 'lucide-react';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

// Subject icons mapping
const SUBJECT_ICONS = {
  history: History,
  geography: Globe,
  polity: Scale,
  economics: TrendingUp,
  science: Beaker
};

const FoundationHubPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [subjects, setSubjects] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalChapters, setTotalChapters] = useState(0);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user, token, navigate]);

  const fetchData = async () => {
    try {
      // Fetch subjects
      const subjectsRes = await fetch(`${API_URL}/api/foundation/subjects`);
      const subjectsData = await subjectsRes.json();
      setSubjects(subjectsData.subjects || []);
      setTotalChapters(subjectsData.total_chapters || 0);
      
      // Fetch user progress
      const progressRes = await fetch(`${API_URL}/api/foundation/progress/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const progressData = await progressRes.json();
      setUserProgress(progressData);
      
    } catch (error) {
      console.error('Error fetching foundation data:', error);
      toast.error('Failed to load foundation course');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-500" />
      </div>
    );
  }

  const overallProgress = userProgress?.overall?.percentage || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="foundation-hub-page">
      
      {/* Vibrant Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate("/dashboard")}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
              <div>
                <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
                  <BookOpen className="w-7 h-7" />
                  UPSC Foundation
                </h1>
                <p className="text-emerald-200 text-sm">Master NCERT Class 6-12 for UPSC</p>
              </div>
            </div>
          </div>
          
          {/* Progress Stats */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-emerald-100">Your Overall Progress</span>
              <span className="text-2xl font-bold">{overallProgress}%</span>
            </div>
            <Progress value={overallProgress} className="h-2 bg-white/20" />
            <p className="text-emerald-200 text-sm mt-2">
              {userProgress?.overall?.completed || 0} / {totalChapters} chapters completed
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Subjects Grid */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-slate-700 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-emerald-600" />
            Choose a Subject
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subjects.map((subject) => {
              const SubjectIcon = SUBJECT_ICONS[subject.id] || BookOpen;
              const subjectProgress = userProgress?.by_subject?.[subject.id];
              const progressPercent = subjectProgress?.percentage || 0;
              
              return (
                <Link 
                  key={subject.id} 
                  to={`/foundation/${subject.id}`}
                  data-testid={`foundation-subject-${subject.id}`}
                >
                  <Card 
                    className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer group h-full"
                    style={{ borderLeftColor: subject.color, borderLeftWidth: '4px' }}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div 
                          className="p-3 rounded-xl"
                          style={{ backgroundColor: `${subject.color}15` }}
                        >
                          <SubjectIcon 
                            className="h-6 w-6" 
                            style={{ color: subject.color }}
                          />
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-1 transition-all" />
                      </div>
                      
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">
                        {subject.name}
                      </h3>
                      <p className="text-slate-500 text-sm mb-4">
                        {subject.books_count} books • {subject.chapters_count} chapters
                      </p>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Progress</span>
                          <span className="text-slate-700">
                            {subjectProgress?.completed || 0}/{subject.chapters_count}
                          </span>
                        </div>
                        <Progress 
                          value={progressPercent} 
                          className="h-2"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Info Section */}
        <Card className="bg-amber-50 border-amber-200 mt-10">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <GraduationCap className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Why NCERT Foundation?
                </h3>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>NCERT is the primary source for UPSC Prelims - 60-70% questions come directly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>AI-generated summaries focus on exam-relevant content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>Track your progress and build a strong foundation systematically</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FoundationHubPage;
